import React, { useCallback, useContext, useMemo, useState } from "react";
import Ink from "react-ink";
import clsx from "clsx";

import { FormContext } from "../../../lib/util/FormUtils";
import Text from "../Text/Text";
import MenuDropdown, {
	MenuDropdownProps,
} from "../../nav/MenuDropdown/MenuDropdown";
import { IconArrowUp } from "../../../assets";
import {
	computeLayoutClasses,
	LayoutProps,
} from "../../../lib/util/ReactUtils";
import { stopPropagation } from "../../../lib/util/Utils";
import Link from "../../nav/Link/Link";

export interface ButtonProps
	extends Omit<
			React.HTMLProps<HTMLButtonElement>,
			"marginWidth" | "marginHeight" | "width" | "color"
		>,
		LayoutProps {
	loading?: boolean;
	icon?: React.ReactNode;
	/** @deprecated use outlined instead */
	inverted?: boolean;
	outlined?: boolean;
	light?: boolean;
	small?: boolean;
	dropdown?: MenuDropdownProps;
	dev?: boolean;
	href?: string;
	variant?: "error" | "success";
}

const ink = <Ink key={Math.random()} />;

const Button: React.FC<ButtonProps> = (props) => {
	const formContext = useContext<any>(FormContext);
	const {
		loading,
		disabled,
		onClick,
		type,
		icon,
		dev,
		inverted,
		outlined,
		light,
		children,
		variant,
		className,
		small,
		href,
		dropdown,
		...otherProps
	} = props;

	const [layoutClassNames, extraProps] = computeLayoutClasses(otherProps);

	const [dropdownOpen, setDropdownOpen] = useState(false);

	const isDisabled = useMemo(() => {
		if (!type || !["submit", "reset"].includes(type) || !formContext)
			return disabled;
		return disabled || formContext.submitDisabled;
	}, [disabled, type, formContext]);

	const isLoading = useMemo(() => {
		if (type !== "submit" || !formContext) return loading;

		return loading || (formContext && formContext.submtting);
	}, [loading, type, formContext]);

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			if (!isDisabled && !isLoading && onClick) {
				onClick(e);
			}
		},
		[isDisabled, isLoading, onClick],
	);

	const content = (
		<>
			{ink}
			{icon &&
				React.cloneElement(icon as any, {
					className: clsx((icon as any).props.className, "Button__icon"),
				})}
			{children && (
				<Text className={clsx({ "--outlined": inverted || outlined })}>
					{children}
				</Text>
			)}
		</>
	);

	const tag = href ? Link : "button";

	return React.createElement(
		tag,
		{
			href: href,
			type: (type || "button") as any,
			onClick: handleClick,
			className: clsx(
				"Button",
				layoutClassNames,
				"--skeletonIgnore",
				{
					"--dropdown-open": dropdownOpen,
					"--dropdown": dropdown,
					"--disabled": isDisabled,
					"--loading": isLoading,
					"--outlined": inverted || outlined,
					"--small": small,
					"--dev": dev,
					"--light": light,
				},
				variant && `--variant-${variant}`,
				className,
			),
			disabled: isDisabled,
			...extraProps,
		},
		dropdown ? (
			<>
				<div className={"Button__section"}>{content}</div>
				<MenuDropdown
					disabled={disabled}
					onVisibleChange={setDropdownOpen}
					className={"Button__section"}
					placement={"bottom-end"}
					tooltipClassName={"ButtonDropdown__tooltip"}
					overflow={true}
					onClick={stopPropagation}
					{...dropdown}
				>
					<Ink />
					<IconArrowUp />
				</MenuDropdown>
			</>
		) : (
			content
		),
	);
};

export default React.memo(Button);
